import { useRef, useState, useLayoutEffect, forwardRef } from 'react'
import './App.css'
import { pages } from './data'

const clamp01 = (v) => Math.max(0, Math.min(1, v))

const NavElement = ({ page, progress = 1, onGoto }) => {
  // Map progress (-1..1, 0 when centered) to derived values used in styles
  const abs = Math.abs(progress)
  const subtitleBaseY = `${abs * 100}%` // 0 when centered, 100% when off-screen
  const subtitleOpacity = 1 - clamp01(abs) // 1 when centered, 0 when off-screen
  const topTitleTranslateY = `-${abs * 100}%` // slides up when off-screen
  const textReveal = 1 - clamp01(abs) // 0..1 used for mask

  // Measure TopTitle height to position Text right underneath it + gap
  const titleRef = useRef(null)
  const [titleHeight, setTitleHeight] = useState(0)
  useLayoutEffect(() => {
    const el = titleRef.current
    if (!el) return
    const update = () => setTitleHeight(el.getBoundingClientRect().height)
    update()
    let ro
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update)
      ro.observe(el)
    } else {
      window.addEventListener('resize', update)
    }
    return () => {
      if (ro) ro.disconnect()
      else window.removeEventListener('resize', update)
    }
  }, [])

  const textTopPadding = `calc(${titleHeight}px + 2rem)`

  const subtitleRef = useRef(null)

  const totalPages = pages.length - 1

  return (
    <div
      className="nav-element"
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'stretch',
        boxSizing: 'border-box',
        position: 'relative',
        overflow: 'hidden'
      }}>
      <div
        className="nav-element-content"
        style={{
          flex: 1,
          padding: page.img ? '2rem 0rem' : '2rem',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
        {/* Page count top-right (skip for TOC page with ind 0) */}
        {page.ind > 0 && !page.toc && (
          <div
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              padding: '0.4rem 0.75rem',
              fontFamily: "'Poppins', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
              fontSize: '13px',
              fontWeight: 500,
              letterSpacing: '0.5px',
              color: 'rgba(255,255,255,0.5)',
              lineHeight: 1,
              zIndex: 20,
              userSelect: 'none',
              opacity: subtitleOpacity
            }}>
            {page.ind}/{totalPages} {page.topTitle ? '- ' + page.topTitle : ''}
          </div>
        )}

        {/* TEXT or TOC MODE */}
        {(page.text || page.toc) && (
          <>
            <TopTitle ref={titleRef} text={page.title} translateY={topTitleTranslateY} opacity={subtitleOpacity} />
            <Text
              opacity={subtitleOpacity}
              revealProgress={textReveal}
              topPadding={textTopPadding}
              text={page.toc ? undefined : page.text}>
              {page.toc && (
                <ul
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.9rem',
                    fontSize: '16px'
                  }}>
                  {pages
                    .filter((p) => !p.toc)
                    .map((pg) => (
                      <li key={pg.key} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <button
                          onClick={() => onGoto && onGoto(pg.ind)}
                          style={{
                            background: 'rgba(255,255,255,0.08)',
                            border: '1px solid rgba(255,255,255,0.15)',
                            color: 'white',
                            padding: '0.65rem 1rem',
                            borderRadius: '12px',
                            fontSize: '14px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            width: '100%',
                            textAlign: 'left',
                            transition: 'background 160ms ease, transform 160ms ease',
                            display: 'flex',
                            justifyContent: 'start',
                            alignItems: 'start',
                            overflow: 'hidden',
                            position: 'relative',
                            whiteSpace: 'pre-line',
                            gap: '0.5rem'
                          }}
                          onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.97)')}
                          onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                          onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                          aria-label={`Gehe zu Seite ${pg.ind} ${pg.title}`}>
                          <b>{pg.ind}.</b> {pg.title}
                        </button>
                      </li>
                    ))}
                  <div style={{ height: '4rem', flexShrink: 0 }} />
                </ul>
              )}
            </Text>
          </>
        )}

        {/* IMAGE MODE */}
        {!page.text && !page.toc && (
          <>
            <Subtitle ref={subtitleRef} text={page.title} baseTranslate={subtitleBaseY} opacity={subtitleOpacity} />
            <img
              src={page.img}
              alt=""
              style={{ width: '100%', height: 'auto', objectFit: 'contain', borderRadius: '0.5rem' }}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default NavElement

const Subtitle = forwardRef(({ text, baseTranslate, opacity }, ref) => {
  // Local toggle to slide the subtitle out of the way without interfering with page progress transform
  const [hidden, setHidden] = useState(false)
  const toggleHidden = (e) => {
    e.stopPropagation()
    setHidden((h) => !h)
  }

  if (!text) return null

  return (
    <div
      ref={ref}
      // Outer wrapper: ONLY handles page switching movement; keep opacity off to avoid backdrop-filter bugs
      style={{
        position: 'absolute',
        left: 0,
        bottom: 0,
        transform: `translateY(${baseTranslate})`,
        width: '100%',
        pointerEvents: 'none',
        zIndex: 1
      }}>
      {/* Inner card: click animation + opacity live here; keeps blur working on first paint */}
      <div
        onClick={toggleHidden}
        style={{
          background: 'linear-gradient(135deg, #0F2027CC 0%, #0F202799 50%, #0F202766 100%)',
          boxShadow: '0px 0px 20px 0px #0F2027CC',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          borderRadius: '20px',
          padding: '2rem',
          boxSizing: 'border-box',
          margin: '2rem',
          width: 'calc(100% - 4rem)',
          color: 'white',
          fontWeight: 600,
          fontFamily: "'Poppins', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
          fontSize: '34px',
          whiteSpace: 'pre-line',
          userSelect: 'none',
          cursor: 'pointer',
          // Independent transform for toggle; use 3d to promote layer
          transform: hidden ? 'translate3d(0,85%,0)' : 'translate3d(0,0,0)',
          transition: 'transform 300ms ease',
          willChange: 'transform, opacity',
          // Opacity applied to inner, not ancestor, so backdrop-filter works at first paint
          opacity,
          // Re-enable pointer events only on the visible card itself
          pointerEvents: 'auto',
          position: 'relative'
        }}>
        {text}
        <div
          style={{
            position: 'absolute',
            right: '0.5rem',
            top: '0.5rem',
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: 18,
            cursor: 'pointer',
            // Hide interactions when fully transparent
            pointerEvents: opacity > 0.05 ? 'auto' : 'none',
            userSelect: 'none'
          }}>
          <NiceChevronSVG
            size={24}
            style={{ transform: hidden ? 'rotate(-90deg)' : 'rotate(90deg)', transition: 'transform 300ms ease' }}
          />
        </div>
      </div>

      {/* Small handle to bring it back when hidden; respects page opacity */}
    </div>
  )
})

export const TopTitle = forwardRef(({ text, translateY, opacity }, ref) => {
  return (
    <div
      ref={ref}
      style={{
        background: 'linear-gradient(to bottom, #0F2027 70%, transparent)',
        zIndex: 1,
        padding: '2rem 0rem 2rem 0rem',
        boxSizing: 'border-box',
        width: '100%',
        alignSelf: 'start',
        position: 'absolute',
        left: 0,
        top: 0,
        transform: `translateY(${translateY})`,
        opacity,
        margin: '2rem',
        width: 'calc(100% - 4rem)',
        color: 'white',
        fontSize: '38px',
        fontWeight: 600,
        fontFamily: "'Poppins', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
        whiteSpace: 'pre-line'
      }}>
      {text}
    </div>
  )
})

// Modified Text to accept children for TOC scenario
const Text = ({ opacity, revealProgress, topPadding, text, children }) => {
  // Build a feathered top-to-bottom mask from revealProgress (0..1)
  const edge = revealProgress * 100
  const feather = 8
  const from = Math.max(0, edge - feather)
  const to = Math.min(100, edge + feather)
  const mask = `linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) ${from}%, rgba(0,0,0,0) ${to}%, rgba(0,0,0,0) 100%)`

  return (
    <div
      className="scrollable-text"
      style={{
        opacity,
        WebkitMaskImage: mask,
        maskImage: mask,
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskSize: '100% 100%',
        maskSize: '100% 100%',
        display: 'flex',
        flexDirection: 'column',
        paddingTop: topPadding, // below TopTitle + gap
        overflowY: 'auto',
        height: '100%',
        boxSizing: 'border-box',
        WebkitOverflowScrolling: 'touch',
        touchAction: 'auto'
      }}>
      {text && (
        <p
          style={{
            color: 'white',
            fontSize: '18px',
            lineHeight: '1.5',
            marginTop: 0,
            maxWidth: '600px',
            whiteSpace: 'pre-line'
          }}>
          {text}
        </p>
      )}
      {!text && children}
      <div
        style={{
          height: '2px',
          width: '20%',
          marginTop: '20px',
          marginBottom: '20px',
          backgroundColor: 'white',
          alignSelf: 'center',
          justifySelf: 'center'
        }}
      />
    </div>
  )
}

const NiceChevronSVG = ({ size = 24, color = 'white', style = {} }) => (
  <svg
    style={{ ...style, opacity: 0.5 }}
    xmlns="http://www.w3.org/2000/svg"
    fill={color}
    viewBox="0 0 24 24"
    width={size}
    height={size}>
    <path d="M8.59 16.59L13.17 12L8.59 7.41L10 6l6 6l-6 6z" />
    <path d="M0 0h24v24H0z" fill="none" />
  </svg>
)
